import type { LotteryRound } from '../services/lotteryService'

export type SetLottoBettingPackage = {
  code: string
  name: string
  packageType: 'RATE_BASED' | 'SET_BASED'
  setConfig?: {
    fixedPrice: number
    fixedPayout?: number
    discount?: number
    finalPrice?: number
    includedPrizeTypes?: Array<{
      prizeTypeCode: string
      prizeTypeName?: string
      digitCount?: number
    }>
  }
  prizeConfigs?: Array<{
    prizeTypeCode: string
    prizeTypeName?: string
    payoutRate?: number
    isActive?: boolean
  }>
  metadata?: {
    displayCard?: {
      title?: string
      payoutAmount?: number
      discountPercent?: number
      fixedPrice?: number
    }
  }
}

export type SetLottoNavigationState = {
  round?: LotteryRound
  package?: SetLottoBettingPackage
  packageCode?: string
  fixedPrice?: number
  backTo?: string
  backLabel?: string
}

const SET_LOTTO_STORAGE_PREFIX = 'user-classic:set-lotto-game:v1'

const SET_SIZE_ALIASES: Record<string, string[]> = {
  LARGE: ['LARGE', 'SET_LARGE', 'ใหญ่'],
  MEDIUM: ['MEDIUM', 'SET_MEDIUM', 'กลาง'],
  SMALL: ['SMALL', 'SET_SMALL', 'เล็ก'],
}

const SET_SIZE_STICKER_PRICES: Record<string, number> = {
  LARGE: 120,
  MEDIUM: 60,
  SMALL: 30,
}

function normalizeHuayId(huayId?: string | { toString?: () => string } | null): string {
  if (!huayId) return ''
  if (typeof huayId === 'string') return huayId
  if (typeof huayId.toString === 'function') return huayId.toString()
  return String(huayId)
}

export function getSetLottoStorageKey(huayId?: string | { toString?: () => string } | null): string {
  const normalized = normalizeHuayId(huayId)
  return normalized ? `${SET_LOTTO_STORAGE_PREFIX}:${normalized}` : SET_LOTTO_STORAGE_PREFIX
}

function positiveNumber(value: unknown): number | null {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function resolvePackageSizeToken(code?: string, name?: string): string | null {
  const combined = `${code || ''} ${name || ''}`.toUpperCase()
  if (/(^|_)LARGE($|_)|SET_LARGE|ชุดใหญ่|ใหญ่/.test(combined)) return 'LARGE'
  if (/(^|_)MEDIUM($|_)|SET_MEDIUM|ชุดกลาง|กลาง/.test(combined)) return 'MEDIUM'
  if (/(^|_)SMALL($|_)|SET_SMALL|ชุดเล็ก|เล็ก/.test(combined)) return 'SMALL'
  return null
}

function resolveSizeTokenFromStickerPrice(price: number): string | null {
  const entry = Object.entries(SET_SIZE_STICKER_PRICES).find(([, stickerPrice]) => stickerPrice === price)
  return entry?.[0] ?? null
}

export function resolveSelectedSetSizeToken(
  state?: SetLottoNavigationState | null,
  pkg?: SetLottoBettingPackage | null
): string | null {
  const fromCodeOrName = resolvePackageSizeToken(
    state?.packageCode || pkg?.code || state?.package?.code,
    state?.package?.name || pkg?.name
  )
  if (fromCodeOrName) return fromCodeOrName

  const hintedPrice = positiveNumber(state?.fixedPrice)
  if (hintedPrice) return resolveSizeTokenFromStickerPrice(hintedPrice)

  return null
}

function packageMatchesSelection(
  pkg?: SetLottoBettingPackage | null,
  packageCode?: string
): boolean {
  if (!pkg?.code) return false

  const normalizedSelection = String(packageCode || '').trim().toUpperCase()
  const normalizedPackage = String(pkg.code).trim().toUpperCase()
  if (!normalizedSelection) return true
  if (normalizedPackage === normalizedSelection) return true

  const selectionSize = resolvePackageSizeToken(normalizedSelection)
  const packageSize = resolvePackageSizeToken(normalizedPackage, pkg.name)
  return Boolean(selectionSize && packageSize && selectionSize === packageSize)
}

export function resolveSelectedSetPackageCode(
  state?: SetLottoNavigationState | null,
  round?: SetRoundHint | null,
  pkg?: SetLottoBettingPackage | null
): string {
  const rawCode = state?.packageCode || pkg?.code || state?.package?.code || ''
  const effectiveCode = resolveEffectiveSetPackageCode(rawCode, round)
  if (effectiveCode && !isGenericSetPackageCode(effectiveCode)) {
    return effectiveCode
  }

  const size = resolveSelectedSetSizeToken(state, pkg)
  const family = inferSetPackageFamilyFromRound(round)
  if (size && family) {
    return `${family}_${size}`
  }

  return effectiveCode
}

type SetRoundHint = Pick<LotteryRound, 'huayName' | 'huayCode' | 'huayTypeName' | 'huayTypeCode'>

function resolvePackagePriceFromObject(pkg?: SetLottoBettingPackage | null): number | null {
  const candidates = [
    pkg?.setConfig?.fixedPrice,
    pkg?.setConfig?.finalPrice,
    pkg?.metadata?.displayCard?.fixedPrice,
  ]

  for (const candidate of candidates) {
    const parsed = positiveNumber(candidate)
    if (parsed) return parsed
  }

  return null
}

export function inferSetPackageFamilyFromRound(round?: SetRoundHint | null): string | null {
  const combined = [
    round?.huayTypeName,
    round?.huayTypeCode,
    round?.huayName,
    round?.huayCode,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  if (/รัฐบาลไทย|governmentthai|setgovernmentthai/.test(combined)) return 'THAI_GOV_SET'
  if (/ฮานอย|hanoi/.test(combined)) return 'HANOI_SET'
  if (/ลาว|lao/.test(combined)) return 'LAO_SET'

  return null
}

export function resolveEffectiveSetPackageCode(
  packageCode?: string,
  round?: SetRoundHint | null
): string {
  const normalized = String(packageCode || '').trim().toUpperCase()
  if (!normalized) return ''

  if (!isGenericSetPackageCode(normalized)) {
    return normalized
  }

  const size = resolvePackageSizeToken(normalized)
  const family = inferSetPackageFamilyFromRound(round)
  if (size && family) {
    return `${family}_${size}`
  }

  return normalized
}

function enrichSetPackageWithPrice(
  pkg: SetLottoBettingPackage,
  priceHint?: number
): SetLottoBettingPackage {
  const resolvedPrice = resolvePackagePriceFromObject(pkg) ?? positiveNumber(priceHint)
  if (!resolvedPrice) return pkg

  return applyResolvedSetPackage(pkg, {
    package: pkg,
    packageCode: pkg.code,
    fixedPrice: resolvedPrice,
  })
}

export function resolveSetPackageFixedPrice(
  pkg?: SetLottoBettingPackage | null,
  state?: SetLottoNavigationState | null
): number {
  const candidates = [
    state?.fixedPrice,
    state?.package?.setConfig?.fixedPrice,
    state?.package?.setConfig?.finalPrice,
    state?.package?.metadata?.displayCard?.fixedPrice,
    pkg?.setConfig?.fixedPrice,
    pkg?.setConfig?.finalPrice,
    pkg?.metadata?.displayCard?.fixedPrice,
  ]

  for (const candidate of candidates) {
    const parsed = positiveNumber(candidate)
    if (parsed) return parsed
  }

  const sizeToken = resolveSelectedSetSizeToken(state, pkg)
  if (sizeToken && SET_SIZE_STICKER_PRICES[sizeToken]) {
    return SET_SIZE_STICKER_PRICES[sizeToken]
  }

  return 0
}

export function hasResolvableSetPackage(
  pkg?: SetLottoBettingPackage | null,
  state?: SetLottoNavigationState | null
): boolean {
  return resolveSetPackageFixedPrice(pkg, state) > 0
}

export function mergeSetLottoState(routeState?: SetLottoNavigationState | null): SetLottoNavigationState {
  const fromRoute = routeState || {}
  const routeHuayId = normalizeHuayId(fromRoute.round?.huayId)
  const storageKey = getSetLottoStorageKey(routeHuayId)

  let fromStorage: SetLottoNavigationState = {}
  try {
    const raw = sessionStorage.getItem(storageKey)
    if (raw) {
      const parsed = JSON.parse(raw) as SetLottoNavigationState
      const storedHuayId = normalizeHuayId(parsed.round?.huayId)
      if (!routeHuayId || storedHuayId === routeHuayId) {
        fromStorage = parsed
      }
    }
  } catch {
    fromStorage = {}
  }

  const routePackage = fromRoute.package
  const storagePackage = fromStorage.package
  const routeSelectionCode =
    fromRoute.packageCode
    || routePackage?.code
    || ''
  const storageSelectionCode =
    fromStorage.packageCode
    || storagePackage?.code
    || ''

  let mergedPackage = routePackage
  if (!mergedPackage && storagePackage) {
    const selectionCode = routeSelectionCode || storageSelectionCode
    if (packageMatchesSelection(storagePackage, selectionCode)) {
      mergedPackage = storagePackage
    }
  }

  const mergedSelectionCode = routeSelectionCode || storageSelectionCode
  const routePrice = resolveSetPackageFixedPrice(routePackage, {
    ...fromRoute,
    packageCode: routeSelectionCode || fromRoute.packageCode,
  })
  const storagePrice = packageMatchesSelection(storagePackage, routeSelectionCode || storageSelectionCode)
    ? resolveSetPackageFixedPrice(storagePackage, {
      ...fromStorage,
      packageCode: storageSelectionCode || fromStorage.packageCode,
    })
    : 0
  const mergedPrice = routePrice > 0 ? routePrice : storagePrice

  const merged: SetLottoNavigationState = {
    ...fromStorage,
    ...fromRoute,
    round: fromRoute.round || fromStorage.round,
    package: mergedPackage,
    packageCode: mergedSelectionCode || undefined,
    fixedPrice: mergedPrice > 0 ? mergedPrice : undefined,
    backTo: fromRoute.backTo || fromStorage.backTo,
    backLabel: fromRoute.backLabel || fromStorage.backLabel,
  }

  if (merged.round && (merged.package || merged.packageCode || merged.fixedPrice)) {
    try {
      sessionStorage.setItem(getSetLottoStorageKey(merged.round.huayId), JSON.stringify(merged))
    } catch {
      // ignore quota errors
    }
  }

  return merged
}

export function matchSetPackageBySelection(
  packages: SetLottoBettingPackage[],
  packageCode?: string,
  fixedPrice?: number
): SetLottoBettingPackage | undefined {
  const priceHint = positiveNumber(fixedPrice)
  const rawSetPackages = packages.filter((pkg) => pkg.packageType === 'SET_BASED' && pkg.code)
  if (rawSetPackages.length === 0) return undefined

  const setPackages = rawSetPackages
    .map((pkg) => enrichSetPackageWithPrice(pkg, priceHint ?? undefined))
    .filter((pkg) => pkg.setConfig && positiveNumber(pkg.setConfig.fixedPrice))

  if (setPackages.length === 0) return undefined

  const normalizedCode = String(packageCode || '').trim().toUpperCase()

  if (normalizedCode) {
    const exact = setPackages.find((pkg) => String(pkg.code || '').trim().toUpperCase() === normalizedCode)
    if (exact) return exact

    const partial = setPackages.find((pkg) => {
      const pkgCode = String(pkg.code || '').toUpperCase()
      return pkgCode.includes(normalizedCode) || normalizedCode.includes(pkgCode)
    })
    if (partial) return partial

    const sizeToken = resolvePackageSizeToken(normalizedCode)
    if (sizeToken) {
      const aliases = SET_SIZE_ALIASES[sizeToken]
      const bySize = setPackages.find((pkg) => {
        const combined = `${pkg.code || ''} ${pkg.name || ''}`.toUpperCase()
        return aliases.some((alias) => combined.includes(alias.toUpperCase()))
      })
      if (bySize) return bySize
    }
  }

  if (priceHint) {
    const byPrice = setPackages.find((pkg) => positiveNumber(pkg.setConfig?.fixedPrice) === priceHint)
    if (byPrice) return byPrice
  }

  return undefined
}

export function applyResolvedSetPackage(
  pkg: SetLottoBettingPackage,
  state?: SetLottoNavigationState | null
): SetLottoBettingPackage {
  const resolvedPrice = resolveSetPackageFixedPrice(pkg, state)
  if (!resolvedPrice) return pkg

  if (pkg.setConfig && positiveNumber(pkg.setConfig.fixedPrice)) return pkg

  return {
    ...pkg,
    packageType: 'SET_BASED',
    setConfig: {
      fixedPrice: resolvedPrice,
      fixedPayout: pkg.setConfig?.fixedPayout ?? 0,
      discount: pkg.setConfig?.discount ?? 0,
      finalPrice: pkg.setConfig?.finalPrice && pkg.setConfig.finalPrice > 0
        ? pkg.setConfig.finalPrice
        : resolvedPrice,
      includedPrizeTypes: pkg.setConfig?.includedPrizeTypes,
    },
  }
}

export function isGenericSetPackageCode(code?: string): boolean {
  return /^SET_(LARGE|MEDIUM|SMALL)$/i.test(String(code || '').trim())
}

export function isHydratedSetPackage(pkg?: SetLottoBettingPackage | null): boolean {
  if (!pkg?.code || !pkg.setConfig) return false
  if (isGenericSetPackageCode(pkg.code)) return false
  return positiveNumber(pkg.setConfig.fixedPrice) !== null
}

export function buildSetPackageFromState(
  state?: SetLottoNavigationState | null
): SetLottoBettingPackage | undefined {
  if (!state) return undefined
  if (state.package) return applyResolvedSetPackage(state.package, state)

  const price = resolveSetPackageFixedPrice(null, state)
  const code = String(state.packageCode || '').trim()
  if (!code || !price) return undefined

  return applyResolvedSetPackage(
    {
      code,
      name: code,
      packageType: 'SET_BASED',
    },
    state
  )
}

export function buildFallbackSetPackageFromState(
  state?: SetLottoNavigationState | null,
  round?: SetRoundHint | null
): SetLottoBettingPackage | undefined {
  if (!state) return undefined

  const selectedSize = resolveSelectedSetSizeToken(state, state.package)
  if (!selectedSize) return undefined

  const effectiveCode = resolveSelectedSetPackageCode(state, round, state.package)
  if (!effectiveCode || isGenericSetPackageCode(effectiveCode)) return undefined

  const price = resolveSetPackageFixedPrice(state.package, {
    ...state,
    packageCode: effectiveCode,
  })
  if (price <= 0) return undefined

  const packageName = state.package?.name || effectiveCode
  const basePackage = state.package && packageMatchesSelection(state.package, effectiveCode)
    ? state.package
    : {
      code: effectiveCode,
      name: packageName,
      packageType: 'SET_BASED' as const,
    }

  return applyResolvedSetPackage(
    {
      ...basePackage,
      code: effectiveCode,
      packageType: 'SET_BASED',
    },
    {
      ...state,
      packageCode: effectiveCode,
      fixedPrice: price,
    }
  )
}

export function buildInitialSetPackage(
  state?: SetLottoNavigationState | null,
  round?: SetRoundHint | null
): SetLottoBettingPackage | undefined {
  const fromState = buildSetPackageFromState(state)
  if (isHydratedSetPackage(fromState)) return fromState

  const fallback = buildFallbackSetPackageFromState(state, round)
  if (isHydratedSetPackage(fallback)) return fallback

  return fromState ?? fallback
}
